import { useMemo, type FC, type ReactNode } from "react";
import type { Key } from "react-aria-components";
import type { Size } from "../helpers/helpers.js";
import { ComboboxField } from "./comboboxField.js";
import {
  useCreatableCombobox,
  type ComboboxCreateOption,
} from "./useCreatableCombobox.js";

const noSuggestions: readonly string[] = [];

interface CreatableOption {
  /** Typed as a Key because the "Add ..." option's own id shares the type. */
  id: Key;
  label: string;
}

const getOptionLabel = (option: CreatableOption | ComboboxCreateOption) =>
  option.label;

// Everything a pasted list names that isn't already an option or a chip is a
// new value; the chips field has matched and deduplicated the rest.
const handlePastedNewValues = (values: readonly string[]) => values;

export interface CreatableComboboxFieldProps {
  /** The chosen values. Free-form text, so a value doubles as its option key. */
  value: readonly string[];
  onChange: (value: string[]) => void;
  /**
   * Existing values to offer in the menu. Anything the user types can be added
   * whether it is among these or not.
   */
  suggestions?: readonly string[];
  label?: string;
  /** Accessible name, for when no visible `label` is rendered. */
  "aria-label"?: string;
  description?: ReactNode;
  /** Shown in place of `description`, and marks the field invalid. */
  errorMessage?: ReactNode;
  placeholder?: string;
  /** Accessible name for the group of chips. Defaults to `Selected {label}`. */
  chipsLabel?: string;
  /**
   * Overrides the `Add "..."` option's label. Keep the typed text in it, or the
   * built-in filter will hide the option. Memoize it: a new function each render
   * rebuilds the option list, and with it react-aria's collection.
   */
  formatCreateLabel?: (label: string) => string;
  /** Rendered when nothing matches the filter. Defaults to "No options found". */
  renderEmptyState?: () => ReactNode;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Multi-select of free-form text: a {@link ComboboxField} whose values are the
 * typed strings themselves, shown as chips.
 *
 * Values are added by picking the "Add ..." option (with the mouse or Enter) or
 * by pasting a comma/semicolon/newline/tab separated list. `suggestions` are
 * offered in the menu but never restrict what can be added — for a fixed list
 * of options, use {@link ComboboxField} instead. Values that differ only in
 * case count as the same value.
 *
 * Fully controlled: reject a value by leaving it out of the next `onChange`
 * value (to validate it, cap the count, show a notification, ...).
 */
export const CreatableComboboxField: FC<CreatableComboboxFieldProps> = (
  props,
) => {
  const {
    value,
    onChange,
    suggestions = noSuggestions,
    formatCreateLabel,
    ...fieldProps
  } = props;

  // Every selected value must be in the collection, or its chip has no label to
  // render — so the options are the suggestions plus whatever is selected.
  // Case-insensitively deduplicated, and a selected value wins over a
  // suggestion it differs from only in case, so its chip keeps rendering.
  const options = useMemo<readonly CreatableOption[]>(() => {
    const chosen = new Map<string, string>();
    for (const text of [...value, ...suggestions]) {
      const key = text.toLowerCase();
      if (!chosen.has(key)) {
        chosen.set(key, text);
      }
    }

    // Suggestions keep their given order; selected values not among them follow.
    const isChosen = (text: string) => chosen.get(text.toLowerCase()) === text;
    const ordered = [
      ...suggestions.filter(isChosen),
      ...value.filter(isChosen),
    ];
    return [...new Set(ordered)].map((text) => ({ id: text, label: text }));
  }, [suggestions, value]);

  const selectedKeys = useMemo<readonly Key[]>(() => [...value], [value]);

  const { comboboxProps } = useCreatableCombobox<CreatableOption, "multiple">({
    items: options,
    getLabel: getOptionLabel,
    value: [...selectedKeys],
    onChange: (keys) => {
      onChange(keys.map(String));
    },
    selectionMode: "multiple",
    // The value is its own key, so there is no separate option list to add to.
    onCreate: (text) => text,
    formatCreateLabel,
  });

  const { defaultItems, ...creatableProps } = comboboxProps;

  return (
    <ComboboxField<CreatableOption, "multiple">
      {...fieldProps}
      {...creatableProps}
      items={defaultItems}
      getLabel={getOptionLabel}
      onPasteUnmatched={handlePastedNewValues}
    />
  );
};
